import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const COLORS = [
  "#3b82f6",
  "#8b5cf6",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
  "#f97316",
  "#6366f1",
];
const STATUS_TO_STAGE = {
  1: "open",
  2: "active",
  3: "resolved",
  4: "closed",
  6: "reopen",
};
const ALLOWED = new Set(["open", "active", "resolved", "closed", "reopen"]);

function polar(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcD(cx, cy, r1, r2, s, e) {
  const sw = e - s;
  if (sw < 0.1) return "";
  if (sw >= 359.9) {
    const ot = polar(cx, cy, r2, 0),
      ob = polar(cx, cy, r2, 180);
    const it = polar(cx, cy, r1, 0),
      ib = polar(cx, cy, r1, 180);
    return [
      `M${ot.x},${ot.y}`,
      `A${r2},${r2} 0 0 1 ${ob.x},${ob.y}`,
      `A${r2},${r2} 0 0 1 ${ot.x},${ot.y}`,
      `L${it.x},${it.y}`,
      `A${r1},${r1} 0 0 0 ${ib.x},${ib.y}`,
      `A${r1},${r1} 0 0 0 ${it.x},${it.y}Z`,
    ].join(" ");
  }
  const os = polar(cx, cy, r2, s),
    oe = polar(cx, cy, r2, e);
  const ie = polar(cx, cy, r1, e),
    is_ = polar(cx, cy, r1, s);
  const lg = sw > 180 ? 1 : 0;
  return `M${os.x},${os.y} A${r2},${r2} 0 ${lg} 1 ${oe.x},${oe.y} L${ie.x},${ie.y} A${r1},${r1} 0 ${lg} 0 ${is_.x},${is_.y}Z`;
}

function shiftColor(hex, amt) {
  const r = Math.min(255, Math.max(0, parseInt(hex.slice(1, 3), 16) + amt));
  const g = Math.min(255, Math.max(0, parseInt(hex.slice(3, 5), 16) + amt));
  const b = Math.min(255, Math.max(0, parseInt(hex.slice(5, 7), 16) + amt));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function cubicEase(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function shortName(name) {
  if (!name) return "Unknown";
  return name
    .split("@")[0]
    .split(".")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function daysRemaining(deadline) {
  if (!deadline) return null;
  const d = new Date(deadline);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / 86400000);
}

function arcLabelD(cx, cy, r, s, e, flip) {
  if (flip) {
    const a = polar(cx, cy, r, e),
      b = polar(cx, cy, r, s);
    const lg = e - s > 180 ? 1 : 0;
    return `M${a.x},${a.y} A${r},${r} 0 ${lg} 0 ${b.x},${b.y}`;
  }
  const a = polar(cx, cy, r, s),
    b = polar(cx, cy, r, e);
  const lg = e - s > 180 ? 1 : 0;
  return `M${a.x},${a.y} A${r},${r} 0 ${lg} 1 ${b.x},${b.y}`;
}

function radialLabelD(cx, cy, r1, r2, deg) {
  const inner = polar(cx, cy, r1, deg),
    outer = polar(cx, cy, r2, deg);
  const norm = ((deg % 360) + 360) % 360;
  if (norm > 180) return `M${outer.x},${outer.y} L${inner.x},${inner.y}`;
  return `M${inner.x},${inner.y} L${outer.x},${outer.y}`;
}

const EWC_CSS = `
.ewc-root {
  --ewc-bg: #eef1f5;
  --ewc-surface: #ffffff;
  --ewc-border: #dde3ea;
  --ewc-green: #22c55e;
  --ewc-red: #ef4444;
  --ewc-accent: #6D17CE;
  display: flex;
  min-height: calc(100vh - 132px);
  background: var(--ewc-bg);
}
@media (max-width: 820px) {
  .ewc-root { flex-direction: column; }
  .ewc-chart-panel { width: 100% !important; min-width: unset !important; }
}
@keyframes ewcClockIn {
  from { transform: rotate(-60deg) scale(0.85); opacity: 0; }
  to   { transform: rotate(0deg) scale(1); opacity: 1; }
}
@keyframes ewcSlideIn {
  from { transform: translateX(8px); opacity: 0; }
  to   { transform: translateX(0); opacity: 1; }
}
.ewc-chart-panel { padding: 16px; flex-shrink: 0; transition: width 0.3s ease; }
.ewc-chart-card {
  background: var(--ewc-surface); border-radius: 16px;
  border: 1px solid var(--ewc-border); padding: 16px;
  position: relative; box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.ewc-chart-enter { animation: ewcClockIn 0.6s ease-out both; transform-origin: center; }
.ewc-chart-svg { width: 100%; height: auto; display: block; }
.ewc-side-panel {
  flex: 1; padding: 16px; overflow-y: auto;
  animation: ewcSlideIn 0.4s ease-out both; min-width: 0;
}
.ewc-eng-arc {
  cursor: pointer;
  transition: transform 0.25s ease, opacity 0.25s ease, filter 0.25s ease;
}
.ewc-ticket-wedge { cursor: pointer; transition: opacity 0.25s ease; }
.ewc-ticket-wedge:hover { opacity: 0.8; }
.ewc-health-arc { cursor: pointer; transition: opacity 0.25s ease; }
.ewc-health-arc:hover { opacity: 0.8; }
.ewc-arc-label {
  font-size: 9px; font-weight: 800; fill: white;
  stroke: #1a1a1a; stroke-width: 2.5px; paint-order: stroke; pointer-events: none;
}
.ewc-radial-label {
  font-size: 6.5px; font-weight: 800; fill: white;
  stroke: #1a1a1a; stroke-width: 2px; paint-order: stroke; pointer-events: none;
}
.ewc-health-label {
  font-size: 8px; font-weight: 800; fill: white;
  stroke: #1a1a1a; stroke-width: 2px; paint-order: stroke; pointer-events: none;
}
.ewc-center-count { font-size: 28px; font-weight: 800; fill: #1e293b; }
.ewc-center-label { font-size: 12px; font-weight: 600; fill: #64748b; }
.ewc-center-count-small { font-size: 13px; font-weight: 700; fill: #64748b; }
.ewc-panel-card {
  background: var(--ewc-surface); border-radius: 12px;
  border: 1px solid var(--ewc-border); padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.ewc-stat-tabs { display: flex; gap: 8px; margin-bottom: 16px; }
.ewc-stat-tab {
  flex: 1; padding: 10px 12px; border-radius: 10px;
  border: 1.5px solid var(--ewc-border); cursor: pointer;
  text-align: center; transition: all 0.2s ease; background: var(--ewc-surface);
}
.ewc-stat-tab:hover { border-color: var(--ewc-accent); }
.ewc-stat-tab.ewc-active { border-color: var(--ewc-accent); background: #f0e8fa; }
.ewc-stat-tab.ewc-green.ewc-active { border-color: var(--ewc-green); background: #f0fdf4; }
.ewc-stat-tab.ewc-red.ewc-active { border-color: var(--ewc-red); background: #fef2f2; }
.ewc-stat-value { font-size: 24px; font-weight: 800; color: #1e293b; }
.ewc-stat-label { font-size: 11px; font-weight: 600; color: #64748b; margin-top: 2px; }
.ewc-title-bar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; background: #f8fafc; border-radius: 8px; margin-bottom: 12px;
}
.ewc-title-text { font-size: 14px; font-weight: 700; color: #1e293b; }
.ewc-clear-btn {
  font-size: 12px; color: var(--ewc-accent); cursor: pointer;
  font-weight: 600; border: none; background: none; padding: 4px 8px;
}
.ewc-clear-btn:hover { text-decoration: underline; }
.ewc-eng-group-header {
  font-size: 13px; font-weight: 800; color: #334155;
  padding: 8px 8px; margin-top: 8px; border-radius: 8px;
  display: flex; align-items: center; justify-content: space-between;
  cursor: pointer; user-select: none; transition: background 0.15s;
}
.ewc-eng-group-header:hover { background: #f8fafc; }
.ewc-eng-group-header-left { display: flex; align-items: center; gap: 8px; }
.ewc-eng-chevron { transition: transform 0.2s ease; flex-shrink: 0; color: #94a3b8; }
.ewc-eng-chevron.ewc-expanded { transform: rotate(90deg); }
.ewc-eng-group-body { overflow: hidden; }
.ewc-expand-all-btn {
  font-size: 12px; color: var(--ewc-accent); cursor: pointer;
  font-weight: 600; border: none; background: none; padding: 4px 8px;
}
.ewc-expand-all-btn:hover { text-decoration: underline; }
.ewc-search-wrap { position: relative; margin-bottom: 12px; }
.ewc-search-input {
  width: 100%; padding: 8px 12px 8px 32px; border-radius: 8px;
  border: 1px solid var(--ewc-border); font-size: 12px; font-weight: 600;
  color: #334155; outline: none; box-sizing: border-box;
}
.ewc-search-input:focus { border-color: var(--ewc-accent); }
.ewc-search-icon {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  color: #94a3b8; pointer-events: none;
}
.ewc-eng-group-header:focus-visible { outline: 2px solid var(--ewc-accent); outline-offset: -2px; }
.ewc-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.ewc-table th {
  text-align: left; padding: 6px 8px; font-weight: 700; color: #64748b;
  border-bottom: 1px solid var(--ewc-border); font-size: 11px;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.ewc-table td { padding: 6px 8px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.ewc-table tr:hover td { background: #f8fafc; }
.ewc-table tr.ewc-selected td { background: #f0e8fa; }
.ewc-ticket-link {
  color: var(--ewc-accent); font-weight: 700; cursor: pointer;
  border: none; background: none; font-size: 12px; padding: 0;
}
.ewc-ticket-link:hover { text-decoration: underline; }
.ewc-priority-badge {
  display: inline-block; padding: 2px 8px; border-radius: 999px;
  font-size: 11px; font-weight: 700; white-space: nowrap;
}
.ewc-priority-critical { background: #fef2f2; color: #ef4444; }
.ewc-priority-high     { background: #fff7ed; color: #f97316; }
.ewc-priority-medium   { background: #fefce8; color: #eab308; }
.ewc-priority-low      { background: #f0fdf4; color: #22c55e; }
.ewc-eta-badge {
  display: inline-block; padding: 2px 8px; border-radius: 999px;
  font-size: 11px; font-weight: 700; white-space: nowrap;
}
.ewc-eta-under { background: #f0fdf4; color: #22c55e; }
.ewc-eta-over  { background: #fef2f2; color: #ef4444; }
.ewc-deadline-badge {
  display: inline-block; padding: 1px 5px; border-radius: 4px;
  font-size: 10px; font-weight: 700; margin-left: 4px;
}
.ewc-days-ok   { background: #f0fdf4; color: #16a34a; }
.ewc-days-warn { background: #fefce8; color: #ca8a04; }
.ewc-days-over { background: #fef2f2; color: #dc2626; }
.ewc-btn {
  display: inline-flex; align-items: center; gap: 4px; padding: 5px 11px;
  border-radius: 7px; font-size: 11px; font-weight: 700;
  border: 1px solid var(--ewc-border); background: var(--ewc-surface);
  cursor: pointer; transition: all 0.15s; color: #334155; flex-shrink: 0;
}
.ewc-btn:hover { border-color: var(--ewc-accent); color: var(--ewc-accent); }
.ewc-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ewc-btn:disabled:hover { border-color: var(--ewc-border); color: #334155; }
.ewc-select {
  padding: 5px 10px; border-radius: 7px; font-size: 11px; font-weight: 600;
  border: 1px solid var(--ewc-border); background: var(--ewc-surface);
  color: #334155; outline: none; width: 132px; max-width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ewc-select:focus { border-color: var(--ewc-accent); }
.ewc-swimlane-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 4px; border: none;
  background: transparent; cursor: pointer; color: #94a3b8;
  transition: all 0.15s; vertical-align: middle; margin-left: 4px;
}
.ewc-swimlane-btn:hover { background: #f1f5f9; color: var(--ewc-accent); }
.ewc-resize-handle {
  position: absolute; bottom: 8px; right: 8px; width: 20px; height: 20px;
  cursor: ew-resize; opacity: 0.3; transition: opacity 0.2s;
}
.ewc-resize-handle:hover { opacity: 0.8; }
.ewc-dim { opacity: 0.3; transition: opacity 0.25s ease; }
.ewc-header-bar { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 12px; }
.ewc-header-left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex: 1; min-width: 0; }
.ewc-header-export { flex-shrink: 0; margin-left: auto; }
.ewc-filters-bar {
  margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid var(--ewc-border);
}
.ewc-filters-label-row {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 8px;
}
.ewc-filter-label { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
.ewc-filters-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.ewc-filters-row .ewc-select { width: 100%; }
.ewc-empty {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 48px 24px; color: #94a3b8; font-size: 14px;
}
.ewc-section-header {
  font-size: 14px; font-weight: 800; padding: 10px 0 6px;
  display: flex; align-items: center; gap: 6px; color: #334155;
}
.ewc-detail-stat-boxes { display: flex; gap: 8px; margin-bottom: 16px; }
.ewc-detail-stat-box {
  flex: 1; padding: 12px; border-radius: 10px;
  border: 1.5px solid var(--ewc-border); text-align: center;
  cursor: pointer; transition: all 0.2s ease; background: var(--ewc-surface);
}
.ewc-detail-stat-box:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.ewc-scrollable { max-height: 300px; overflow-y: auto; }
`;

const CX = 250,
  CY = 250;
const DEFAULT_CHART_WIDTH_PCT = 38;

export const EngineerWorkloadChart = ({
  tickets = [],
  projects = [],
  priorities = [],
  requestOptions = [],
  statuses = [],
}) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const uidRef = useRef("ewc" + Math.random().toString(36).slice(2, 8));
  const uid = uidRef.current;

  const [selectedProject, setSelectedProject] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [requestTypeFilter, setRequestTypeFilter] = useState("All");
  const [viewStack, setViewStack] = useState([{ mode: "overview" }]);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [etaFilter, setEtaFilter] = useState(null);
  const [chartWidthPct, setChartWidthPct] = useState(DEFAULT_CHART_WIDTH_PCT);
  const [animKey, setAnimKey] = useState(0);
  const [expandedEngineers, setExpandedEngineers] = useState(new Set());
  const didInitExpansionRef = useRef(false);
  const [engineerSearch, setEngineerSearch] = useState("");
  const sidePanelRef = useRef(null);

  const currentView = viewStack[viewStack.length - 1];
  const isDetail = currentView.mode === "detail";

  const lookupPriority = useCallback(
    (id) => {
      const p = priorities.find((x) => String(x.value) === String(id));
      return p ? p.key : String(id);
    },
    [priorities],
  );

  const lookupRequest = useCallback(
    (id) => {
      const r = requestOptions.find((x) => String(x.value) === String(id));
      return r ? r.key : String(id);
    },
    [requestOptions],
  );

  const filteredTickets = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return tickets
      .filter(
        (t) =>
          ALLOWED.has(STATUS_TO_STAGE[t.status]) &&
          (selectedProject === "All" || t.org === selectedProject) &&
          (priorityFilter === "All" ||
            String(t.priority) === String(priorityFilter)) &&
          (requestTypeFilter === "All" ||
            String(t.requestType) === String(requestTypeFilter)),
      )
      .map((t) => {
        let over = false;
        if (t.deadline) {
          const d = new Date(t.deadline);
          if (!isNaN(d.getTime()) && d < today) over = true;
        }
        return { ...t, etaStatus: over ? "overETA" : "underETA" };
      });
  }, [tickets, selectedProject, priorityFilter, requestTypeFilter]);

  const engineerData = useMemo(() => {
    const g = {};
    for (const t of filteredTickets) {
      const k = t.assign || "Unassigned";
      if (!g[k]) g[k] = { name: k, tickets: [], under: [], over: [] };
      g[k].tickets.push(t);
      (t.etaStatus === "overETA" ? g[k].over : g[k].under).push(t);
    }
    return Object.values(g).sort((a, b) => b.tickets.length - a.tickets.length);
  }, [filteredTickets]);

  const totalUnder = useMemo(
    () => filteredTickets.filter((t) => t.etaStatus === "underETA").length,
    [filteredTickets],
  );
  const totalOver = useMemo(
    () => filteredTickets.filter((t) => t.etaStatus === "overETA").length,
    [filteredTickets],
  );

  const segments = useMemo(() => {
    if (!engineerData.length || !filteredTickets.length) return [];
    const gap = 1.5;
    const total = filteredTickets.length;
    const avail = 360 - engineerData.length * gap;
    let a = 0;
    return engineerData.map((eng, i) => {
      const sw = (eng.tickets.length / total) * avail;
      const seg = {
        ...eng,
        startDeg: a,
        endDeg: a + sw,
        color: COLORS[i % COLORS.length],
        idx: i,
      };
      a += sw + gap;
      return seg;
    });
  }, [engineerData, filteredTickets]);

  useEffect(() => {
    if (!didInitExpansionRef.current && segments.length > 0) {
      didInitExpansionRef.current = true;
      setExpandedEngineers(new Set([segments[0].name]));
    }
  }, [segments]);

  useEffect(() => {
    if (!selectedTicketId) return;
    const owner = filteredTickets.find((t) => t.ticketId === selectedTicketId);
    if (owner) {
      const key = owner.assign || "Unassigned";
      setExpandedEngineers((prev) => new Set(prev).add(key));
    }
  }, [selectedTicketId, filteredTickets]);

  useEffect(() => {
    if (!selectedTicketId) return;
    const raf = requestAnimationFrame(() => {
      const el = sidePanelRef.current?.querySelector(
        `[data-ticket-row="${selectedTicketId}"]`,
      );
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedTicketId, expandedEngineers]);

  const toggleEngineer = (name) => {
    setExpandedEngineers((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const goDetail = (name) => {
    setViewStack((v) => [...v, { mode: "detail", engineer: name }]);
    setEtaFilter(null);
    setSelectedTicketId(null);
    setAnimKey((k) => k + 1);
  };
  const goBack = () => {
    if (viewStack.length > 1) {
      setViewStack((v) => v.slice(0, -1));
      setEtaFilter(null);
      setSelectedTicketId(null);
      setAnimKey((k) => k + 1);
    }
  };
  const resetAll = () => {
    setViewStack([{ mode: "overview" }]);
    setEtaFilter(null);
    setSelectedTicketId(null);
    setHoveredIdx(null);
    setEngineerSearch("");
    setAnimKey((k) => k + 1);
  };

  const ACTIVE_STAGES = new Set(["open", "active", "reopen"]);
  const RESOLVED_STAGES = new Set(["resolved", "closed"]);

  const downloadExcel = useCallback(() => {
    const summaryRows = engineerData.map((eng, i) => {
      const active = eng.tickets.filter((t) =>
        ACTIVE_STAGES.has(STATUS_TO_STAGE[t.status]),
      ).length;
      const resolved = eng.tickets.filter((t) =>
        RESOLVED_STAGES.has(STATUS_TO_STAGE[t.status]),
      ).length;
      return {
        "S. No.": i + 1,
        "Assigned To": shortName(eng.name),
        Active: active,
        Resolved: resolved,
        Total: eng.tickets.length,
      };
    });
    summaryRows.push({
      "S. No.": "",
      "Assigned To": "Grand Total",
      Active: summaryRows.reduce((s, r) => s + r.Active, 0),
      Resolved: summaryRows.reduce((s, r) => s + r.Resolved, 0),
      Total: summaryRows.reduce((s, r) => s + r.Total, 0),
    });

    const detailRows = filteredTickets.map((t, i) => ({
      "S. No.": i + 1,
      "Ticket ID": t.ticketId || "",
      Title: t.title || "",
      "Assigned To": shortName(t.assign),
      Application: t.org || "",
      "Request Type": lookupRequest(t.requestType),
      Priority: lookupPriority(t.priority),
      Stage: (STATUS_TO_STAGE[t.status] || "").replace(/^\w/, (c) =>
        c.toUpperCase(),
      ),
      ETA: t.etaStatus === "underETA" ? "On Track" : "Overdue",
      Deadline: t.deadline
        ? new Date(t.deadline).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "—",
      "Created Date": t.createdAt
        ? new Date(t.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "—",
    }));

    const teamMap = {};
    for (const t of filteredTickets) {
      const team = t.org || "Unknown";
      if (!teamMap[team])
        teamMap[team] = {
          active: 0,
          resolved: 0,
          total: 0,
          engineers: new Set(),
        };
      teamMap[team].total++;
      teamMap[team].engineers.add(t.assign || "Unassigned");
      if (ACTIVE_STAGES.has(STATUS_TO_STAGE[t.status])) teamMap[team].active++;
      else if (RESOLVED_STAGES.has(STATUS_TO_STAGE[t.status]))
        teamMap[team].resolved++;
    }
    const teamRows = Object.entries(teamMap)
      .sort((a, b) => b[1].total - a[1].total)
      .map(([team, d], i) => ({
        "S. No.": i + 1,
        Team: team,
        Engineers: d.engineers.size,
        Active: d.active,
        Resolved: d.resolved,
        Total: d.total,
      }));
    teamRows.push({
      "S. No.": "",
      Team: "Grand Total",
      Engineers: new Set(filteredTickets.map((t) => t.assign || "Unassigned"))
        .size,
      Active: teamRows.reduce((s, r) => s + (r.Active || 0), 0),
      Resolved: teamRows.reduce((s, r) => s + (r.Resolved || 0), 0),
      Total: teamRows.reduce((s, r) => s + (r.Total || 0), 0),
    });

    const wb = XLSX.utils.book_new();

    const ws1 = XLSX.utils.json_to_sheet(summaryRows);
    ws1["!cols"] = [
      { wch: 6 },
      { wch: 24 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
    ];
    XLSX.utils.book_append_sheet(wb, ws1, "Engineer Summary");

    const ws2 = XLSX.utils.json_to_sheet(detailRows);
    ws2["!cols"] = [
      { wch: 6 },
      { wch: 14 },
      { wch: 36 },
      { wch: 20 },
      { wch: 18 },
      { wch: 16 },
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
      { wch: 16 },
      { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(wb, ws2, "Ticket Details");

    const ws3 = XLSX.utils.json_to_sheet(teamRows);
    ws3["!cols"] = [
      { wch: 6 },
      { wch: 24 },
      { wch: 12 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
    ];
    XLSX.utils.book_append_sheet(wb, ws3, "Team Summary");

    const projectTag =
      selectedProject === "All"
        ? "All_Projects"
        : selectedProject.replace(/\s+/g, "_");
    XLSX.writeFile(
      wb,
      `Workload_Report_${projectTag}_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }, [
    engineerData,
    filteredTickets,
    selectedProject,
    lookupPriority,
    lookupRequest,
  ]);

  const onResize = useCallback(
    (e) => {
      e.preventDefault();
      const sx = e.clientX;
      const sw = chartWidthPct;
      const move = (ev) => {
        if (!containerRef.current) return;
        const cw = containerRef.current.offsetWidth;
        setChartWidthPct(
          Math.min(70, Math.max(20, sw + ((ev.clientX - sx) / cw) * 100)),
        );
      };
      const up = () => {
        document.removeEventListener("mousemove", move);
        document.removeEventListener("mouseup", up);
      };
      document.addEventListener("mousemove", move);
      document.addEventListener("mouseup", up);
    },
    [chartWidthPct],
  );

  const priBadge = (name) => {
    const n = String(name).toLowerCase();
    if (n.includes("critical")) return "ewc-priority-critical";
    if (n.includes("high")) return "ewc-priority-high";
    if (n.includes("medium")) return "ewc-priority-medium";
    return "ewc-priority-low";
  };

  const renderRow = (t) => {
    const days = daysRemaining(t.deadline);
    const priName = lookupPriority(t.priority);
    return (
      <tr
        key={t.ticketId || t._id}
        data-ticket-row={t.ticketId}
        className={selectedTicketId === t.ticketId ? "ewc-selected" : ""}
      >
        <td style={{ whiteSpace: "nowrap" }}>
          <button
            className="ewc-ticket-link"
            onClick={() => navigate(`/Ticket/${t.ticketId}`)}
          >
            {t.ticketId}
          </button>
          <button
            className="ewc-swimlane-btn"
            title="View Ticket"
            onClick={() => navigate(`/Ticket/${t.ticketId}`)}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </button>
        </td>
        <td>{lookupRequest(t.requestType)}</td>
        <td>
          <span className={`ewc-priority-badge ${priBadge(priName)}`}>
            {priName}
          </span>
        </td>
        <td>{t.org || "\u2014"}</td>
        <td>
          <span
            className={`ewc-eta-badge ${
              t.etaStatus === "underETA" ? "ewc-eta-under" : "ewc-eta-over"
            }`}
          >
            {t.etaStatus === "underETA" ? "On Track" : "Overdue"}
          </span>
        </td>
        <td style={{ whiteSpace: "nowrap" }}>
          {t.deadline
            ? new Date(t.deadline).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "\u2014"}
          {days !== null && (
            <span
              className={`ewc-deadline-badge ${
                days > 3
                  ? "ewc-days-ok"
                  : days >= 0
                    ? "ewc-days-warn"
                    : "ewc-days-over"
              }`}
            >
              {days > 0
                ? `${days}d left`
                : days === 0
                  ? "Today"
                  : `${Math.abs(days)}d over`}
            </span>
          )}
        </td>
      </tr>
    );
  };

  const tableHead = (
    <thead>
      <tr>
        <th>Ticket</th>
        <th>Type</th>
        <th>Priority</th>
        <th>Project</th>
        <th>ETA</th>
        <th>Deadline</th>
      </tr>
    </thead>
  );

  /* ────────────────── Overview SVG ────────────────── */
  const renderOverviewSVG = () => {
    if (!segments.length) {
      return (
        <div className="ewc-empty">
          <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
          <p>No ticket data available</p>
        </div>
      );
    }

    const defs = [];
    const engArcs = [];
    const engLabels = [];
    const ticketArcs = [];
    const ticketLabels = [];
    const healthArcs = [];
    const healthLabels = [];

    segments.forEach((seg, i) => {
      const mid = (seg.startDeg + seg.endDeg) / 2;
      const flip = mid > 90 && mid < 270;
      const elpId = `${uid}elp${i}`;

      defs.push(
        <path
          key={elpId}
          id={elpId}
          d={arcLabelD(CX, CY, 110, seg.startDeg, seg.endDeg, flip)}
          fill="none"
        />,
      );

      engArcs.push(
        <path
          key={`ea${i}`}
          d={arcD(CX, CY, 80, 140, seg.startDeg, seg.endDeg)}
          fill={seg.color}
          className="ewc-eng-arc"
          style={
            hoveredIdx === i
              ? {
                  transform: "scale(1.045)",
                  transformOrigin: `${CX}px ${CY}px`,
                  filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.3))",
                }
              : hoveredIdx !== null
                ? { opacity: 0.35 }
                : {}
          }
          onMouseEnter={() => setHoveredIdx(i)}
          onMouseLeave={() => setHoveredIdx(null)}
          onClick={() => goDetail(seg.name)}
        >
          <title>
            {`${shortName(seg.name)} — ${seg.tickets.length} ticket${seg.tickets.length !== 1 ? "s" : ""} (${seg.under.length} on track, ${seg.over.length} overdue)`}
          </title>
        </path>,
      );

      const engSw = seg.endDeg - seg.startDeg;
      if (engSw > 8) {
        engLabels.push(
          <text key={`et${i}`} className="ewc-arc-label">
            <textPath href={`#${elpId}`} startOffset="50%" textAnchor="middle">
              {shortName(seg.name)} ({seg.tickets.length})
            </textPath>
          </text>,
        );
      }

      const tCount = seg.tickets.length;
      const tSweep = engSw / Math.max(1, tCount);

      seg.tickets.forEach((t, j) => {
        const ts = seg.startDeg + j * tSweep;
        const te = ts + tSweep;
        const shade =
          t.etaStatus === "underETA"
            ? shiftColor(seg.color, 60)
            : shiftColor(seg.color, -40);

        ticketArcs.push(
          <path
            key={`tw${i}_${j}`}
            d={arcD(CX, CY, 146, 210, ts, te)}
            fill={shade}
            className="ewc-ticket-wedge"
            onClick={() => setSelectedTicketId(t.ticketId)}
          >
            <title>
              {`${t.ticketId} — ${t.etaStatus === "underETA" ? "On track" : "Overdue"}`}
            </title>
          </path>,
        );

        if (tSweep > 4) {
          const tmid = ts + tSweep / 2;
          const rlId = `${uid}rl${i}_${j}`;
          defs.push(
            <path
              key={rlId}
              id={rlId}
              d={radialLabelD(CX, CY, 150, 206, tmid)}
              fill="none"
            />,
          );
          ticketLabels.push(
            <text key={`tl${i}_${j}`} className="ewc-radial-label">
              <textPath
                href={`#${rlId}`}
                startOffset="50%"
                textAnchor="middle"
                lengthAdjust="spacingAndGlyphs"
              >
                {t.ticketId}
              </textPath>
            </text>,
          );
        }
      });

      const uSw = tCount > 0 ? (seg.under.length / tCount) * engSw : 0;

      if (seg.under.length > 0) {
        healthArcs.push(
          <path
            key={`hu${i}`}
            d={arcD(CX, CY, 216, 236, seg.startDeg, seg.startDeg + uSw)}
            fill="var(--ewc-green)"
            className="ewc-health-arc"
            onClick={() => setEtaFilter(etaFilter === "under" ? null : "under")}
          >
            <title>{`${shortName(seg.name)} — ${seg.under.length} on track`}</title>
          </path>,
        );
        if (uSw > 12) {
          const hp = polar(CX, CY, 226, seg.startDeg + uSw / 2);
          healthLabels.push(
            <text
              key={`hlu${i}`}
              x={hp.x}
              y={hp.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="ewc-health-label"
            >
              {seg.under.length}
            </text>,
          );
        }
      }

      if (seg.over.length > 0) {
        healthArcs.push(
          <path
            key={`ho${i}`}
            d={arcD(CX, CY, 216, 236, seg.startDeg + uSw, seg.endDeg)}
            fill="var(--ewc-red)"
            className="ewc-health-arc"
            onClick={() => setEtaFilter(etaFilter === "over" ? null : "over")}
          >
            <title>{`${shortName(seg.name)} — ${seg.over.length} overdue`}</title>
          </path>,
        );
        const oSw = engSw - uSw;
        if (oSw > 12) {
          const hp = polar(CX, CY, 226, seg.startDeg + uSw + oSw / 2);
          healthLabels.push(
            <text
              key={`hlo${i}`}
              x={hp.x}
              y={hp.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="ewc-health-label"
            >
              {seg.over.length}
            </text>,
          );
        }
      }
    });

    return (
      <svg viewBox="0 0 500 500" className="ewc-chart-svg">
        <defs>{defs}</defs>
        {engArcs}
        {ticketArcs}
        {healthArcs}
        {engLabels}
        {ticketLabels}
        {healthLabels}
        <circle cx={CX} cy={CY} r={74} fill="white" />
        <text
          x={CX}
          y={CY - 12}
          textAnchor="middle"
          className="ewc-center-count"
        >
          {engineerData.length}
        </text>
        <text
          x={CX}
          y={CY + 6}
          textAnchor="middle"
          className="ewc-center-label"
        >
          Engineers
        </text>
        <text
          x={CX}
          y={CY + 24}
          textAnchor="middle"
          className="ewc-center-count-small"
        >
          {filteredTickets.length} tickets
        </text>
      </svg>
    );
  };

  /* ────────────────── Detail SVG ────────────────── */
  const renderDetailSVG = () => {
    const eng = engineerData.find((e) => e.name === currentView.engineer);
    if (!eng) {
      return (
        <div className="ewc-empty">
          <p>Engineer not found</p>
        </div>
      );
    }
    const ci = engineerData.indexOf(eng);
    const color = COLORS[ci % COLORS.length];
    const total = eng.tickets.length;
    if (!total) return null;

    const tSweep = 360 / total;
    const uSw = (eng.under.length / total) * 360;

    const defs = [];
    const wedges = [];
    const wLabels = [];

    eng.tickets.forEach((t, j) => {
      const ts = j * tSweep;
      const te = ts + tSweep;
      const shade =
        t.etaStatus === "underETA"
          ? shiftColor(color, 60)
          : shiftColor(color, -40);

      wedges.push(
        <path
          key={`dw${j}`}
          d={arcD(CX, CY, 138, 210, ts, te)}
          fill={shade}
          className="ewc-ticket-wedge"
          onClick={() => setSelectedTicketId(t.ticketId)}
        >
          <title>
            {`${t.ticketId} — ${t.etaStatus === "underETA" ? "On track" : "Overdue"}`}
          </title>
        </path>,
      );

      if (tSweep > 5) {
        const midA = ts + tSweep / 2;
        const flip = midA > 90 && midA < 270;
        const lpId = `${uid}dlp${j}`;
        defs.push(
          <path
            key={lpId}
            id={lpId}
            d={arcLabelD(CX, CY, 174, ts, te, flip)}
            fill="none"
          />,
        );
        wLabels.push(
          <text
            key={`dwl${j}`}
            className="ewc-radial-label"
            style={{ fontSize: "7.5px" }}
          >
            <textPath href={`#${lpId}`} startOffset="50%" textAnchor="middle">
              {t.ticketId}
            </textPath>
          </text>,
        );
      }
    });

    const underHealthLabel =
      eng.under.length > 0
        ? (() => {
            const p = polar(CX, CY, 228, uSw / 2);
            return (
              <text
                x={p.x}
                y={p.y}
                textAnchor="middle"
                dominantBaseline="central"
                className="ewc-health-label"
              >
                {eng.under.length}
              </text>
            );
          })()
        : null;

    const overHealthLabel =
      eng.over.length > 0
        ? (() => {
            const p = polar(CX, CY, 228, uSw + (360 - uSw) / 2);
            return (
              <text
                x={p.x}
                y={p.y}
                textAnchor="middle"
                dominantBaseline="central"
                className="ewc-health-label"
              >
                {eng.over.length}
              </text>
            );
          })()
        : null;

    return (
      <svg viewBox="0 0 500 500" className="ewc-chart-svg">
        <defs>{defs}</defs>
        <path d={arcD(CX, CY, 70, 130, 0, 360)} fill={color} />
        {wedges}
        {eng.under.length > 0 && (
          <path
            d={arcD(CX, CY, 216, 240, 0, uSw)}
            fill="var(--ewc-green)"
            className="ewc-health-arc"
            onClick={() => setEtaFilter(etaFilter === "under" ? null : "under")}
          >
            <title>{`${eng.under.length} on track`}</title>
          </path>
        )}
        {eng.over.length > 0 && (
          <path
            d={arcD(CX, CY, 216, 240, uSw, 360)}
            fill="var(--ewc-red)"
            className="ewc-health-arc"
            onClick={() => setEtaFilter(etaFilter === "over" ? null : "over")}
          >
            <title>{`${eng.over.length} overdue`}</title>
          </path>
        )}
        {wLabels}
        {underHealthLabel}
        {overHealthLabel}
        <circle cx={CX} cy={CY} r={64} fill="white" />
        <text
          x={CX}
          y={CY - 8}
          textAnchor="middle"
          style={{ fontSize: 14, fontWeight: 800, fill: "#1e293b" }}
        >
          {shortName(eng.name)}
        </text>
        <text
          x={CX}
          y={CY + 14}
          textAnchor="middle"
          className="ewc-center-count-small"
        >
          {total} tickets
        </text>
      </svg>
    );
  };

  /* ────────────────── Overview Panel ────────────────── */
  const renderOverviewPanel = () => {
    const contextLabel = selectedTicketId
      ? `Ticket: ${selectedTicketId}`
      : etaFilter === "under"
        ? "Under ETA"
        : etaFilter === "over"
          ? "Over ETA"
          : "All Engineers";
    const showClear = selectedTicketId || etaFilter;

    const displayData = segments
      .map((seg) => {
        let tix = seg.tickets;
        if (etaFilter === "under") tix = seg.under;
        else if (etaFilter === "over") tix = seg.over;
        return { ...seg, displayTickets: tix };
      })
      .filter((s) => s.displayTickets.length > 0);

    const searchLower = engineerSearch.trim().toLowerCase();
    const searchedData = searchLower
      ? displayData.filter((s) =>
          shortName(s.name).toLowerCase().includes(searchLower),
        )
      : displayData;

    const allExpanded =
      searchedData.length > 0 &&
      searchedData.every((s) => expandedEngineers.has(s.name));

    const toggleAll = () => {
      if (allExpanded) {
        setExpandedEngineers((prev) => {
          const next = new Set(prev);
          searchedData.forEach((s) => next.delete(s.name));
          return next;
        });
      } else {
        setExpandedEngineers(
          (prev) => new Set([...prev, ...searchedData.map((s) => s.name)]),
        );
      }
    };

    return (
      <div className="ewc-panel-card">
        <div className="ewc-stat-tabs">
          <div
            className={`ewc-stat-tab ${!etaFilter ? "ewc-active" : ""}`}
            onClick={() => {
              setEtaFilter(null);
              setSelectedTicketId(null);
            }}
          >
            <div className="ewc-stat-value">{filteredTickets.length}</div>
            <div className="ewc-stat-label">Total</div>
          </div>
          <div
            className={`ewc-stat-tab ewc-green ${
              etaFilter === "under" ? "ewc-active" : ""
            }`}
            onClick={() => {
              setEtaFilter(etaFilter === "under" ? null : "under");
              setSelectedTicketId(null);
            }}
          >
            <div
              className="ewc-stat-value"
              style={{ color: "var(--ewc-green)" }}
            >
              {totalUnder}
            </div>
            <div className="ewc-stat-label">Under ETA</div>
          </div>
          <div
            className={`ewc-stat-tab ewc-red ${
              etaFilter === "over" ? "ewc-active" : ""
            }`}
            onClick={() => {
              setEtaFilter(etaFilter === "over" ? null : "over");
              setSelectedTicketId(null);
            }}
          >
            <div className="ewc-stat-value" style={{ color: "var(--ewc-red)" }}>
              {totalOver}
            </div>
            <div className="ewc-stat-label">Over ETA</div>
          </div>
        </div>

        <div className="ewc-title-bar">
          <span className="ewc-title-text">{contextLabel}</span>
          <div style={{ display: "flex", alignItems: "center" }}>
            {searchedData.length > 0 && (
              <button className="ewc-expand-all-btn" onClick={toggleAll}>
                {allExpanded ? "Collapse all" : "Expand all"}
              </button>
            )}
            {showClear && (
              <button
                className="ewc-clear-btn"
                onClick={() => {
                  setSelectedTicketId(null);
                  setEtaFilter(null);
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {displayData.length > 1 && (
          <div className="ewc-search-wrap">
            <svg
              className="ewc-search-icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="ewc-search-input"
              placeholder="Search engineer..."
              aria-label="Search engineer"
              value={engineerSearch}
              onChange={(e) => setEngineerSearch(e.target.value)}
            />
          </div>
        )}

        {searchedData.map((seg) => {
          const expanded = expandedEngineers.has(seg.name);
          return (
            <div key={seg.name}>
              <div
                className="ewc-eng-group-header"
                style={{ borderLeft: `3px solid ${seg.color}`, paddingLeft: 8 }}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={() => toggleEngineer(seg.name)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleEngineer(seg.name);
                  }
                }}
              >
                <span className="ewc-eng-group-header-left">
                  <svg
                    className={`ewc-eng-chevron ${expanded ? "ewc-expanded" : ""}`}
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <polyline points="9 6 15 12 9 18" />
                  </svg>
                  {shortName(seg.name)}
                </span>
                <span>
                  {seg.displayTickets.length} ticket
                  {seg.displayTickets.length !== 1 ? "s" : ""}
                </span>
              </div>
              {expanded && (
                <div className="ewc-eng-group-body">
                  <table className="ewc-table">
                    {tableHead}
                    <tbody>{seg.displayTickets.map((t) => renderRow(t))}</tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {displayData.length === 0 && (
          <div className="ewc-empty">
            <p>No tickets match the current filter</p>
          </div>
        )}

        {displayData.length > 0 && searchedData.length === 0 && (
          <div className="ewc-empty">
            <p>No engineers match "{engineerSearch}"</p>
          </div>
        )}
      </div>
    );
  };

  /* ────────────────── Detail Panel ────────────────── */
  const renderDetailPanel = () => {
    const eng = engineerData.find((e) => e.name === currentView.engineer);
    if (!eng) return null;
    const ci = engineerData.indexOf(eng);
    const color = COLORS[ci % COLORS.length];

    return (
      <div className="ewc-panel-card">
        <div
          style={{
            fontSize: 18,
            fontWeight: 800,
            marginBottom: 12,
            color: "#1e293b",
          }}
        >
          {shortName(eng.name)}
        </div>

        <div className="ewc-detail-stat-boxes">
          <div
            className="ewc-detail-stat-box"
            onClick={() => setEtaFilter(null)}
            style={!etaFilter ? { borderColor: color } : {}}
          >
            <div className="ewc-stat-value">{eng.tickets.length}</div>
            <div className="ewc-stat-label">Total</div>
          </div>
          <div
            className="ewc-detail-stat-box"
            onClick={() => setEtaFilter(etaFilter === "under" ? null : "under")}
            style={
              etaFilter === "under" ? { borderColor: "var(--ewc-green)" } : {}
            }
          >
            <div
              className="ewc-stat-value"
              style={{ color: "var(--ewc-green)" }}
            >
              {eng.under.length}
            </div>
            <div className="ewc-stat-label">Under ETA</div>
          </div>
          <div
            className="ewc-detail-stat-box"
            onClick={() => setEtaFilter(etaFilter === "over" ? null : "over")}
            style={
              etaFilter === "over" ? { borderColor: "var(--ewc-red)" } : {}
            }
          >
            <div className="ewc-stat-value" style={{ color: "var(--ewc-red)" }}>
              {eng.over.length}
            </div>
            <div className="ewc-stat-label">Over ETA</div>
          </div>
        </div>

        <div className={etaFilter === "over" ? "ewc-dim" : ""}>
          <div className="ewc-section-header">
            <span style={{ color: "var(--ewc-green)" }}>{"\u25CF"}</span>
            Under ETA ({eng.under.length})
          </div>
          <div className="ewc-scrollable">
            <table className="ewc-table">
              {tableHead}
              <tbody>{eng.under.map((t) => renderRow(t))}</tbody>
            </table>
            {eng.under.length === 0 && (
              <div
                className="ewc-empty"
                style={{ padding: "16px 0", fontSize: 12 }}
              >
                No tickets under ETA
              </div>
            )}
          </div>
        </div>

        <div
          className={etaFilter === "under" ? "ewc-dim" : ""}
          style={{ marginTop: 16 }}
        >
          <div className="ewc-section-header">
            <span style={{ color: "var(--ewc-red)" }}>{"\u25CF"}</span>
            Over ETA ({eng.over.length})
          </div>
          <div className="ewc-scrollable">
            <table className="ewc-table">
              {tableHead}
              <tbody>{eng.over.map((t) => renderRow(t))}</tbody>
            </table>
            {eng.over.length === 0 && (
              <div
                className="ewc-empty"
                style={{ padding: "16px 0", fontSize: 12 }}
              >
                No tickets over ETA
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ────────────────── Main Render ────────────────── */
  return (
    <>
      <style>{EWC_CSS}</style>
      <div className="ewc-root" ref={containerRef}>
        <div
          className="ewc-chart-panel"
          style={{ width: `${chartWidthPct}%`, minWidth: 380 }}
        >
          <div className="ewc-chart-card">
            <div className="ewc-header-bar">
              <div className="ewc-header-left">
                {viewStack.length > 1 && (
                  <button className="ewc-btn" onClick={goBack}>
                    ← Back
                  </button>
                )}
                <select
                  className="ewc-select"
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                >
                  <option value="All">All Organisations</option>
                  {projects.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <button className="ewc-btn" onClick={resetAll}>
                  Reset
                </button>
              </div>
              <button
                className="ewc-btn ewc-header-export"
                onClick={downloadExcel}
                disabled={filteredTickets.length === 0}
                title={
                  filteredTickets.length === 0
                    ? "No data to export"
                    : "Download Excel report"
                }
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export
              </button>
            </div>
            <div className="ewc-filters-bar">
              <div className="ewc-filters-label-row">
                <span className="ewc-filter-label">Filters</span>
                {(priorityFilter !== "All" || requestTypeFilter !== "All") && (
                  <button
                    className="ewc-clear-btn"
                    onClick={() => {
                      setPriorityFilter("All");
                      setRequestTypeFilter("All");
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
              <div className="ewc-filters-row">
                <select
                  className="ewc-select"
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <option value="All">All Priorities</option>
                  {priorities.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.key}
                    </option>
                  ))}
                </select>
                <select
                  className="ewc-select"
                  value={requestTypeFilter}
                  onChange={(e) => setRequestTypeFilter(e.target.value)}
                >
                  <option value="All">All Request Types</option>
                  {requestOptions.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.key}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="ewc-chart-enter" key={animKey}>
              {isDetail ? renderDetailSVG() : renderOverviewSVG()}
            </div>
            <div
              className="ewc-resize-handle"
              onMouseDown={onResize}
              onDoubleClick={() => setChartWidthPct(DEFAULT_CHART_WIDTH_PCT)}
              title="Drag to resize · double-click to reset"
            >
              <svg width="16" height="16" viewBox="0 0 16 16">
                <path
                  d="M14 14L8 14M14 14L14 8M14 14L6 6"
                  stroke="#94a3b8"
                  strokeWidth="1.5"
                  fill="none"
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          className="ewc-side-panel"
          ref={sidePanelRef}
          key={isDetail ? "detail" : "overview"}
        >
          {isDetail ? renderDetailPanel() : renderOverviewPanel()}
        </div>
      </div>
    </>
  );
};
